import { useState } from 'react'
import { Button as TamaguiButton, Spinner } from 'tamagui'
import type { ButtonProps as TamaguiButtonProps } from 'tamagui'

export type ButtonProps = TamaguiButtonProps & {
    /**
     * Show a spinner and disable the button while an external async operation
     * is in flight — e.g. a tRPC/react-query mutation (`loading={m.isPending}`)
     * or a `type="submit"` button driven by a form's `onSubmit`.
     *
     * Async `onPress` handlers are detected automatically and don't need this;
     * use `loading` when the button isn't the thing kicking off the promise.
     */
    loading?: boolean
}

export default function Button({ onPress, disabled, icon, children, size = '$3', loading: loadingProp = false, ...props }: ButtonProps) {
    const [asyncLoading, setAsyncLoading] = useState(false)

    // Either source of truth puts the button into the loading state: an
    // explicit `loading` prop, or a detected async `onPress`.
    const loading = loadingProp || asyncLoading

    const handlePress = async (e: any) => {
        if (loading || !onPress) return
        const result = (onPress as Function)(e)
        if (result && typeof result === 'object' && typeof result.then === 'function') {
            setAsyncLoading(true)
            try {
                await result
            } catch (err) {
                // Tamagui calls onPress fire-and-forget, so surface async handler
                // failures here — otherwise the rejection is unobserved. We log
                // rather than re-throw (a re-throw would just become an unhandled
                // rejection one level up).
                console.error('Button onPress handler failed:', err)
            } finally {
                setAsyncLoading(false)
            }
        }
    }

    return (
        <TamaguiButton
            size={size}
            {...props}
            disabled={disabled || loading}
            // No explicit color: as an `icon`, Tamagui tints the Spinner with the
            // button's text color, so it stays visible on any theme.
            icon={loading ? <Spinner /> : icon}
            onPress={handlePress}
        >
            {children}
        </TamaguiButton>
    )
}
